/**
 * SmartInspect Executive Report tab — displays the finalized report snapshot
 * and offers a "Download PDF" action that streams the server-rendered PDF from
 * the shared Playwright pipeline (identical mechanism to SmartEvaluate).
 *
 * When `viewingVersion` is provided (from the Version History drawer) it shows
 * that immutable finalized snapshot read-only, with a banner + version-specific
 * PDF download.
 */
import { useState, useCallback } from "react";
import { Download, Loader2, FileText, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { siReportPdfUrl, siVersionPdfUrl, siEmailVersionReport } from "../lib/smartInspectApi";
import { SmartInspectResults } from "./SmartInspectResults";

export default function SmartInspectExecutiveReportTab({ assessment, viewingVersion, onExitVersion, onGotoResults }) {
  const viewing = !!viewingVersion;
  const snapshot = viewing ? viewingVersion.snapshot : assessment?.report_snapshot;
  const isFinalized = viewing || ((assessment?.status === "finalized" || assessment?.is_finalized) && snapshot);
  const [downloading, setDownloading] = useState(false);
  const versionId = viewing ? viewingVersion.id : assessment?.finalized_version_id;

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", cc: "", message: "" });
  const [emailing, setEmailing] = useState(false);

  const pdfUrl = viewing
    ? siVersionPdfUrl(assessment?.id, viewingVersion.id)
    : (assessment?.id ? siReportPdfUrl(assessment.id) : "");

  const handleDownload = useCallback(async () => {
    if (downloading || !pdfUrl) return;
    setDownloading(true);
    const toastId = toast.loading("Generating Executive Report PDF…");
    try {
      const res = await fetch(pdfUrl, { credentials: "include" });
      const ctype = res.headers.get("content-type") || "";
      if (!res.ok || !ctype.includes("application/pdf")) {
        let msg = `PDF generation failed (HTTP ${res.status}).`;
        try { const body = await res.json(); if (body?.detail) msg = typeof body.detail === "string" ? body.detail : msg; } catch { /* noop */ }
        toast.error("PDF generation failed. Please try again.", { id: toastId, description: msg });
        return;
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        toast.error("PDF generation failed.", { id: toastId, description: "The generated file was empty." });
        return;
      }
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m?.[1] || "SmartInspect_ROI_Business_Case.pdf";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Executive Report downloaded.", { id: toastId });
    } catch (e) {
      toast.error("PDF generation failed. Please try again.", { id: toastId, description: String(e?.message || e).slice(0, 160) });
    } finally {
      setDownloading(false);
    }
  }, [pdfUrl, downloading]);

  const openEmail = () => {
    const contact = snapshot?.answers?.business_email || snapshot?.answers?.contact_email || "";
    setEmailForm((f) => ({ to: f.to || contact, cc: f.cc || "", message: f.message || "" }));
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    const to = (emailForm.to || "").trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { toast.error("Please enter a valid recipient email"); return; }
    if (!assessment?.id || !versionId) { toast.error("No finalized version loaded"); return; }
    setEmailing(true);
    const toastId = toast.loading("Rendering & emailing report…");
    try {
      const data = await siEmailVersionReport(assessment.id, versionId, {
        to, cc: emailForm.cc?.trim() || undefined, message: emailForm.message?.trim() || undefined,
      });
      if (data?.status === "stub_or_failed") {
        toast.success("Report queued (email provider not configured — stub)", { id: toastId });
      } else {
        toast.success(`Emailed to ${to}`, { id: toastId });
      }
      setEmailOpen(false);
      setEmailForm((f) => ({ ...f, message: "" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to email report", { id: toastId });
    } finally {
      setEmailing(false);
    }
  };

  if (!isFinalized) {
    return (
      <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] p-12 text-center" data-testid="si-report-empty">
        <div className="mx-auto w-12 h-12 rounded-full bg-[#F0F9FF] flex items-center justify-center mb-3">
          <FileText className="w-6 h-6 text-[#025979]" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-medium text-[#111827]">No finalized Executive Report yet.</p>
        <p className="text-sm text-[#6B7280] mt-1">Finalize the report from the ROI Results tab to generate the client-ready business case.</p>
        <Button variant="outline" className="mt-4" data-testid="si-report-goto-results" onClick={() => onGotoResults?.()}>
          Go to ROI Results
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="si-report-tab" className="space-y-4">
      {viewing && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#FDBA74] bg-[#FFF7ED] px-3 py-2" data-testid="si-report-version-banner">
          <span className="text-[12px] text-[#9A3412] inline-flex items-center gap-1.5 font-medium">
            <Lock className="w-3.5 h-3.5" /> Viewing Finalized Version v{viewingVersion.version_number} (Read-only)
          </span>
          <Button size="sm" variant="outline" className="text-[11px] h-7 px-2 border-[#FDBA74] text-[#9A3412] bg-white" onClick={() => onExitVersion?.()} data-testid="si-report-exit-version-btn">
            Return to Latest
          </Button>
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button data-testid="si-report-email-btn" variant="outline" onClick={openEmail} className="border-[#025979] text-[#025979]">
          <Mail className="w-4 h-4 mr-1.5" /> Email Report
        </Button>
        <Button data-testid="si-report-download-btn" onClick={handleDownload} disabled={downloading} className="bg-[#025979] hover:bg-[#03738C] text-white">
          {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Download PDF
        </Button>
      </div>
      <SmartInspectResults result={snapshot?.roi_result} embedded hideZeroSavings />
      <SupportingInformation snapshot={snapshot} viewing={viewing} viewingVersion={viewingVersion} assessment={assessment} />

      <Dialog open={emailOpen} onOpenChange={(v) => !emailing && setEmailOpen(v)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="si-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>Email Executive Report</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280] pt-1">
              The finalized report PDF (v{viewing ? viewingVersion.version_number : (assessment?.report_version || 1)}) will be attached and sent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium text-[#374151] uppercase tracking-wide">To</label>
              <input data-testid="si-email-to" type="email" value={emailForm.to} onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                placeholder="client@company.com" className="mt-1 w-full h-9 px-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#025979]/30" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#374151] uppercase tracking-wide">CC (optional)</label>
              <input data-testid="si-email-cc" value={emailForm.cc} onChange={(e) => setEmailForm((f) => ({ ...f, cc: e.target.value }))}
                placeholder="colleague@company.com" className="mt-1 w-full h-9 px-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#025979]/30" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-[#374151] uppercase tracking-wide">Message (optional)</label>
              <textarea data-testid="si-email-message" rows={3} value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Add a short note to the client…" className="mt-1 w-full px-3 py-2 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#025979]/30" />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setEmailOpen(false)} disabled={emailing} data-testid="si-email-cancel-btn">Cancel</Button>
            <Button size="sm" className="text-xs bg-[#025979] hover:bg-[#024a63] text-white" onClick={handleSendEmail} disabled={emailing} data-testid="si-email-send-btn">
              {emailing ? "Sending…" : "Send Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── Supporting / current-situation information ───────────────────────────
 * Read-only context block appended below the ROI results (matches Step 4
 * data — no recomputation; values come straight from the finalized snapshot).
 */
const SI_ACTIVITY_KEYS = [
  ["paper_report_time", "Paper inspection reports"],
  ["photo_transfer_time", "Photograph transfer"],
  ["photo_upload_time", "Photograph upload"],
  ["manual_data_entry_time", "Manual / Excel data entry"],
  ["report_email_time", "Report preparation & email"],
  ["sap_erp_entry_time", "SAP / ERP data entry"],
];

const siFmtCur = (cur, v) => {
  if (v === null || v === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(v);
  } catch { return `${cur} ${Number(v).toLocaleString()}`; }
};
const siFmtNum = (v, d = 0) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const siFmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

function SiField({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-sm text-[#111827] font-medium mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function SupportingInformation({ snapshot, viewing, viewingVersion, assessment }) {
  const meta = snapshot?.metadata || {};
  const ans = snapshot?.answers || {};
  const roi = snapshot?.roi_result || {};
  const A = roi.annual_results || {};
  const cur = meta.preferred_currency || roi.preferred_currency || "USD";
  const locations = Number(ans.operating_locations) || 0;
  const activities = SI_ACTIVITY_KEYS.filter(([k]) => Number(ans[k]) > 0).map(([, label]) => label);
  const version = viewing ? viewingVersion?.version_number : (meta.report_version || assessment?.report_version || 1);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="si-supporting-info">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-4">Supporting Information</h3>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <SiField label="Company" value={meta.company_name || ans.company_name} testid="si-support-company" />
        <SiField label="Contact" value={meta.contact_name || ans.contact_name} testid="si-support-contact" />
        <SiField label="Industry" value={meta.industry || ans.industry} testid="si-support-industry" />
        <SiField label="Country" value={meta.country || ans.country} testid="si-support-country" />
        <SiField label="Preferred Currency" value={cur} testid="si-support-currency" />
        <SiField label="Selected SmartInspect Package" value={meta.selected_package_name || roi.selected_package_name} testid="si-support-package" />
        <SiField label="Estimated Annual Inspection Volume" value={siFmtNum(meta.annual_inspection_volume ?? A.inspection_volume)} testid="si-support-volume" />
        <SiField label="Assessment Date" value={siFmtDate(meta.assessment_date)} testid="si-support-date" />
        <SiField label="Report Version" value={`v${version}`} testid="si-support-version" />
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-3">Current Situation</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <SiField label="Operating Locations" value={siFmtNum(locations)} />
          <SiField label="Annual Inspections" value={siFmtNum(A.inspection_volume)} />
          <SiField label="Current Inspection Time" value={`${siFmtNum(A.current_time_per_inspection, 1)} min / inspection`} />
          <SiField label="Current Inspection Hours (Annual)" value={`${siFmtNum(A.current_hours)} hrs`} />
          <SiField label="Current Labour Cost (Annual)" value={siFmtCur(cur, A.current_labour_cost)} />
        </div>
        {activities.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1.5">Existing Manual Activities</div>
            <div className="flex flex-wrap gap-2" data-testid="si-support-activities">
              {activities.map((a) => (
                <span key={a} className="inline-flex items-center text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">{a}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
